import { Block } from '@components/core-ui/Block';
import { Select } from '@components/core-ui/form-elements/Select';
import { Switch } from '@components/core-ui/form-elements/Switch';
import { TextDatePicker } from '@components/core-ui/form-elements/TextDatePicker';
import { TimeInput } from '@components/core-ui/form-elements/TimeInput';
import { TimeZoneSelect } from '@components/core-ui/form-elements/TimeZoneSelect';
import { jobIsScheduled } from '@helpers/jobStatus/jobIsScheduled';
import AddressIcon from '@components/icons/PageAddress.svg';
import LinkIcon from '@components/icons/PageLink.svg';
import { usePageContext } from '@pages/User/NewJobPage/providers/PageProvider';
import React from 'react';
import { ReporterAssigneeBlock } from '../ReporterAssigneeBlock';
import { DatePickerWrapper } from './styles/DatePickerWrapper';
import { FlexTimeWrapper } from './styles/FlexTimeWrapper';
import { Input } from '@components/core-ui/form-elements/Input';

const inputWithLeadingIconClassName = 'relative [&_input]:pl-11';
const leadingIconClassName = 'pointer-events-none absolute left-[10px] top-1/2 z-10 h-6 w-6 -translate-y-1/2';

export const ScheduleBlock = () => {
  const { form, setForm, setFormItemValue, isLoading, job, setJob, isNew } = usePageContext();

  const showMeetingLocation = form.item?.meetingType !== 'RECORDING_SESSION_REMOTE';
  const showMeetingLink = form.item?.meetingType !== 'RECORDING_SESSION_IN_PERSON';

  const handleInputChange = async (evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = evt.target;
    await setFormItemValue(name, value);
  };

  const handleTimeChange = async (value: string) => {
    await setFormItemValue('meetingTime', value);
  };

  const handleMeetingTypeChange = async (item: { label: string; value: string }) => {
    await setFormItemValue('meetingType', item?.value === 'null' ? null : item?.value);
  };
  const handleTimeZoneChange = async (_name: string, value: string) => {
    await setFormItemValue('meetingTimezone', value === 'null' ? null : value);
  };

  const switchStatus = async () => {
    const newStatus = form.item?.status === 'JOB_STATUS_SCHEDULED' ? 'JOB_STATUS_DRAFT' : 'JOB_STATUS_SCHEDULED';
    if (newStatus === 'JOB_STATUS_DRAFT') {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setForm({
        ...form,
        item: {
          ...form.item,
          meetingAddress: '',
          meetingLink: '',
          meetingType: undefined,
          meetingDate: undefined,
          meetingTime: '12:00',
          meetingTimezone: tz,
          meetingAssigneeId: undefined,
          status: newStatus,
          layoutId: undefined,
        },
        meetingAssignee: undefined,
      });
      if (job) {
        setJob({
          ...job,
          meetingAssignee: undefined,
        });
      }
    } else {
      setForm({
        ...form,
        item: {
          ...form.item,
          status: newStatus,
          layoutId: undefined,
        },
      });
    }
  };

  return (
    <div>
      {isNew ? (
        <Block title="Schedule AutoScriptOne Realtime">
          <div />
          <Switch
            checked={!!(form.item?.status && jobIsScheduled(form.item?.status))}
            onChange={switchStatus}
            testId="schedule-switch"
          />
        </Block>
      ) : null}

      {form.item?.status && jobIsScheduled(form.item?.status) ? (
        <Block title="Schedule">
          <Select
            name="meetingType"
            label="Meeting type"
            testId="meeting-type-select"
            required={true}
            value={form.item?.meetingType ?? ''}
            disabled={isLoading}
            onChange={(label, value) => handleMeetingTypeChange({ label, value })}
            options={[
              {
                value: 'null',
                label: 'Not set',
              },
              {
                value: 'RECORDING_SESSION_REMOTE',
                label: 'Remote',
              },
              {
                value: 'RECORDING_SESSION_HYBRID',
                label: 'Hybrid',
              },
              {
                value: 'RECORDING_SESSION_IN_PERSON',
                label: 'In Person',
              },
            ]}
          />

          {form.item?.meetingType?.length ? (
            <>
              <DatePickerWrapper>
                <TextDatePicker
                  label="Date"
                  required={true}
                  minDate={new Date()}
                  maxDate={new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)}
                  value={form.item?.meetingDate ? form.item.meetingDate.split('T')[0] : ''}
                  onChange={(date: string) => {
                    if (!date.length) {
                      setFormItemValue('meetingDate', '');
                      return;
                    }
                    setFormItemValue('meetingDate', date);
                  }}
                />
              </DatePickerWrapper>

              <FlexTimeWrapper>
                <TimeInput
                  onChange={handleTimeChange}
                  required={true}
                  label="Time"
                  value={form.item?.meetingTime ?? '12:00'}
                />

                <TimeZoneSelect
                  onChange={handleTimeZoneChange}
                  required={true}
                  name="meetingTimeZone"
                  label="Time zone"
                  value={form.item?.meetingTimezone ?? ''}
                />
              </FlexTimeWrapper>

              {showMeetingLocation ? (
                <div className={inputWithLeadingIconClassName}>
                  <AddressIcon className={leadingIconClassName} />
                  <Input
                    onChange={handleInputChange}
                    required={(form.item?.meetingType ?? '') !== 'RECORDING_SESSION_HYBRID'}
                    name="meetingAddress"
                    label="Address"
                    value={form.item?.meetingAddress ?? ''}
                  />
                </div>
              ) : null}
              {showMeetingLink ? (
                <div className={inputWithLeadingIconClassName}>
                  <LinkIcon className={leadingIconClassName} />
                  <Input
                    onChange={handleInputChange}
                    required={(form.item?.meetingType ?? '') !== 'RECORDING_SESSION_HYBRID'}
                    name="meetingLink"
                    label="Meeting link"
                    value={form.item?.meetingLink ?? ''}
                  />
                </div>
              ) : null}
            </>
          ) : null}
        </Block>
      ) : null}

      {form.item?.meetingType?.length ? <ReporterAssigneeBlock /> : null}
    </div>
  );
};
